import { BILL_PAYMENT_ROUTES, STATUS_CODE, TOP_NATIVE_ROUTES } from '@/domains/CommonDomain'
import {
  BILLS_IDENTIFIER_TYPE_CODE,
  BILLS_PRODUCT_GROUP_TYPE_CODE,
  sivOmniChannelTelkomsel,
  sKeyOmniChannelTelkomsel,
} from '@/domains/version/V2/BillPaymentDomain'
import { IRootState } from '@/modules'
import { Error, errorActions } from '@/modules/common/error'
import { billPaymentAmountActions } from '@/modules/version/V2/bill-payment/BillPaymentAmountModule'
import {
  billPaymentMainSelector,
  IFlattenProductType,
} from '@/modules/version/V2/bill-payment/BillPaymentMainModule'
import { aes256EncryptTelkomsel, getFormattedCardNo } from '@/utils/formatUtils'
import { push } from '@/utils/historyUtils'
import { isEmpty, isOrUnderLength, isValidRange } from '@/utils/validationUtils'
import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import configs from '@/configs'
import { loadingActions } from '@/modules/common/loading'
import { newError } from '@/utils/errorUtils'
import { fetchProfile, IFindProfileResponse } from '@/api/ProfileApi'
import * as Bridge from '@/api/BridgeApi'

export const BILL_PAYMENT_PRODUCT = 'BillPaymentProduct'

const SUBMIT = `${BILL_PAYMENT_PRODUCT}/submit`
const INPUT_CUSTOMER_ID = `${BILL_PAYMENT_PRODUCT}/inputCustomerId`
const FORCEERROR_CUSTOMER_ID = `${BILL_PAYMENT_PRODUCT}/forceErrorCustomerId`
const FORCEERROR_RESET = `${BILL_PAYMENT_PRODUCT}/forceErrorReset`
const EXCEPTION_ERROR = `${BILL_PAYMENT_PRODUCT}/exceptionError`
const SET_VIEW = `${BILL_PAYMENT_PRODUCT}/setView`
const CLEAR_VIEW = `${BILL_PAYMENT_PRODUCT}/clearView`
const REMOVE_PAYMENT_LAYER = `${BILL_PAYMENT_PRODUCT}/removePaymentLayer`

const INPUT_TOPUP_PRODUCT = `${BILL_PAYMENT_PRODUCT}/inputTopupProductCode`
export const SELECT_TELKOMSEL_MENARIK_PRODUCT = `${BILL_PAYMENT_PRODUCT}/selectTelkomselWebAppProduct`

const CUSTOMER_ID_MIN_LENGTH = 1
const CUSTOMER_ID_MAX_LENGTH = 20
export const CREDIT_CARD_MIN_LENGTH = 18
const CREDIT_CARD_MAX_LENGTH = 19
const PHONE_NUMBER_PREFIX = '08'
const PHONE_NUMBER_MIN_LENGTH = 10
const PHONE_NUMBER_MAX_LENGTH = 15
const VA_NUMBER_MAX_LENGTH = 16

interface IBillPaymentProductSubmitAction {
  productGroupTypeCode: string
  customerName?: string
  ticketInfo1?: string
  ticketInfo2?: string
  ticketInfo3?: string
  ticketInfo4?: string
  merchantName?: string
}

export interface IBillPaymentProductView {
  visiblePaymentLayer: boolean
  customerId: string
  customerName?: string
  ticketInfo1?: string
  ticketInfo2?: string
  ticketInfo3?: string
  ticketInfo4?: string
  selectedProductGroupTypeCode: string
  selectedProductTypeCode: string
  selectedProductTypeName: string
  selectedIdentifierType: string
  selectedIdentifierName: string
  invalidCustomerId: boolean
  errorCode?: string
  errorMsg?: string
}

export interface IBillPaymentProductState {
  isValid: boolean
  view: IBillPaymentProductView
}

export const initialState: IBillPaymentProductState = {
  // TODO validation 방법 고민
  isValid: false,
  view: {
    visiblePaymentLayer: false,
    customerId: '',
    customerName: '',
    ticketInfo1: '',
    ticketInfo2: '',
    ticketInfo3: '',
    ticketInfo4: '',
    selectedProductGroupTypeCode: '',
    selectedProductTypeCode: '',
    selectedProductTypeName: '',
    selectedIdentifierType: '',
    selectedIdentifierName: '',
    invalidCustomerId: false,
    errorCode: '',
    errorMsg: '',
  },
}

export const billPaymentProductActions = {
  setView: createAction<Partial<IBillPaymentProductView>>(SET_VIEW),
  inputCustomerId: createAction<string>(INPUT_CUSTOMER_ID),
  forceErrorCustomerId: createAction<string>(FORCEERROR_CUSTOMER_ID),
  forceErrorReset: createAction<string>(FORCEERROR_RESET),
  exceptionError: createAction<object>(EXCEPTION_ERROR),
  submit: createAction<IBillPaymentProductSubmitAction>(SUBMIT),
  clearView: createAction(CLEAR_VIEW),
  inputTopupProductCd: createAction<string>(INPUT_TOPUP_PRODUCT),
  removePaymentLayer: createAction(REMOVE_PAYMENT_LAYER),
  selectTelkomselMenarikProduct: createAction<string>(SELECT_TELKOMSEL_MENARIK_PRODUCT),
}

const reducer = {
  [SET_VIEW]: (
    state: IBillPaymentProductState,
    action: Action<Partial<IBillPaymentProductView>>,
  ) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_VIEW]: (state: IBillPaymentProductState) => ({
    ...state,
    view: initialState.view,
  }),
  [REMOVE_PAYMENT_LAYER]: (state: IBillPaymentProductState) => ({
    ...state,
    view: {
      ...state.view,
      visiblePaymentLayer: false,
    },
  }),
}

export const billPaymentProductReducer = handleActions(reducer, initialState)

export const billPaymentProductSelector = {
  isValid: (state: IRootState) => state[BILL_PAYMENT_PRODUCT].isValid,
  view: (state: IRootState) => state[BILL_PAYMENT_PRODUCT].view ?? initialState.view,
  isNumberKeyEnabled: (state: IRootState, identifierType: string): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view
    let rtnFlag = true

    if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.PHONE) {
      rtnFlag = isOrUnderLength(customerId, PHONE_NUMBER_MAX_LENGTH - 1)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD) {
      rtnFlag = isOrUnderLength(customerId, CREDIT_CARD_MAX_LENGTH - 1)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.CUSTOMERID) {
      rtnFlag = isOrUnderLength(customerId, CUSTOMER_ID_MAX_LENGTH - 1)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.AACTSHF) {
      rtnFlag = isOrUnderLength(customerId, CUSTOMER_ID_MAX_LENGTH - 1)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.VA) {
      rtnFlag = isOrUnderLength(customerId, VA_NUMBER_MAX_LENGTH - 1)
    }

    return rtnFlag
  },
  isValidPlnCustomerId: (state: IRootState): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view

    return isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
  },
  isValidPhoneNumber: (state: IRootState): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view
    if (!customerId.startsWith(PHONE_NUMBER_PREFIX)) {
      return false
    }

    return isValidRange(PHONE_NUMBER_MIN_LENGTH, PHONE_NUMBER_MAX_LENGTH)(customerId.length)
  },
  isValidCustomerId: (state: IRootState, productGroupTypeCode: string): boolean => {
    const isPln = productGroupTypeCode === BILLS_PRODUCT_GROUP_TYPE_CODE.PLN

    if (isPln) {
      return billPaymentProductSelector.isValidPlnCustomerId(state)
    }

    return billPaymentProductSelector.isValidPhoneNumber(state)
  },
  isValidCustomerIdSize: (state: IRootState, identifierType: string): boolean => {
    const { customerId } = state[BILL_PAYMENT_PRODUCT].view
    let rtnFlag = false
    if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.PHONE) {
      rtnFlag = isValidRange(PHONE_NUMBER_MIN_LENGTH, PHONE_NUMBER_MAX_LENGTH)(customerId.length)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD) {
      rtnFlag = isValidRange(CREDIT_CARD_MIN_LENGTH, CREDIT_CARD_MAX_LENGTH)(customerId.length)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.CUSTOMERID) {
      rtnFlag = isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
    } else if (identifierType === BILLS_IDENTIFIER_TYPE_CODE.AACTSHF) {
      rtnFlag = isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
    } else {
      rtnFlag = isValidRange(CUSTOMER_ID_MIN_LENGTH, CUSTOMER_ID_MAX_LENGTH)(customerId.length)
    }

    return rtnFlag
  },
  getTopupProductCd: (state: IRootState): string => {
    const { selectedProductTypeCode } = state[BILL_PAYMENT_PRODUCT].view

    return selectedProductTypeCode
  },
  isPln: (state: IRootState): boolean => {
    const { selectedProductGroupTypeCode } = state[BILL_PAYMENT_PRODUCT].view ?? initialState.view
    return selectedProductGroupTypeCode === BILLS_PRODUCT_GROUP_TYPE_CODE.PLN
  },
}

function* inputCustomerId(action: Action<string>) {
  const customerId = action.payload

  if (isEmpty(customerId)) {
    yield put(billPaymentProductActions.setView({ invalidCustomerId: false }))
  }

  yield put(
    billPaymentProductActions.setView({
      customerId,
      invalidCustomerId: false,
    }),
  )
}

function* forceErrorCustomerId(action: Action<string>) {
  const customerId = action.payload

  yield put(
    billPaymentProductActions.setView({
      customerId,
      invalidCustomerId: true,
    }),
  )
}

function* forceErrorReset(action: Action<String>) {
  yield put(
    billPaymentProductActions.setView({
      errorCode: '',
      errorMsg: '',
      invalidCustomerId: false,
    }),
  )
}

function* exceptionError(action: Action<Error>) {
  const errorCode = action.payload.code + ''
  const errorMsg = action.payload.message

  yield put(
    billPaymentProductActions.setView({
      errorCode,
      errorMsg,
    }),
  )
}

function* inputTopupProductCd(action: Action<string>) {
  const topupProductCd = action.payload

  yield put(billPaymentProductActions.inputTopupProductCd(topupProductCd))
}

function* submit(action: Action<IBillPaymentProductSubmitAction>) {
  const {
    productGroupTypeCode,
    customerName,
    ticketInfo1,
    ticketInfo2,
    ticketInfo3,
    ticketInfo4,
    merchantName,
  } = action.payload

  const isValidCustomerId: boolean = yield select(
    billPaymentProductSelector.isValidCustomerId,
    productGroupTypeCode,
  )

  // zakat은 customerid 입력 체크 제외
  if (productGroupTypeCode !== '0310') {
    if (!isValidCustomerId) {
      yield put(billPaymentProductActions.setView({ invalidCustomerId: false }))
    }
  }

  const {
    customerId,
    selectedProductTypeCode: productTypeCode,
  }: IBillPaymentProductView = yield select(billPaymentProductSelector.view)

  const { productTypeIdentifierType }: IFlattenProductType = yield select(
    billPaymentMainSelector.flattenProductType,
    productTypeCode,
  )

  const formattedCustomerId =
    productTypeIdentifierType === BILLS_IDENTIFIER_TYPE_CODE.CREDITCARD
      ? getFormattedCardNo(customerId)
      : customerId

  yield put(billPaymentAmountActions.setCustomerId(formattedCustomerId))
  yield put(billPaymentAmountActions.setCustomerName(customerName ?? ''))
  yield put(
    billPaymentAmountActions.setTicketInfo({
      ticketInfo1: ticketInfo1 ?? '',
      ticketInfo2: ticketInfo2 ?? '',
      ticketInfo3: ticketInfo3 ?? '',
      ticketInfo4: ticketInfo4 ?? '',
      merchantName: merchantName ?? '',
    }),
  )

  yield call(push, `${BILL_PAYMENT_ROUTES.AMOUNT}/${productTypeCode}`)
}

function* selectTelkomselMenarik(action: Action<string>) {
  yield put(loadingActions.start(SELECT_TELKOMSEL_MENARIK_PRODUCT))
  try {
    const phoneNumber = action.payload
    const { phoneNo }: IFindProfileResponse = yield call(fetchProfile)
    if (!phoneNo) throw newError(STATUS_CODE.NO_DATA)
    const data = `uid=${phoneNo};msisdn=${phoneNumber}`
    const encrypted = aes256EncryptTelkomsel(
      sKeyOmniChannelTelkomsel,
      sivOmniChannelTelkomsel,
      data,
    )
    const url = `${configs.TELKOMSEL_MENARIK_LINK}?data=${encodeURIComponent(encrypted)}`
    yield Bridge.navigateTo(TOP_NATIVE_ROUTES.WEBVIEW, false, url)
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(SELECT_TELKOMSEL_MENARIK_PRODUCT))
  }
}

export const billPaymentProductSaga = [
  takeLatest(INPUT_CUSTOMER_ID, inputCustomerId),
  takeLatest(SUBMIT, submit),
  takeLatest(INPUT_TOPUP_PRODUCT, inputTopupProductCd),
  takeLatest(FORCEERROR_CUSTOMER_ID, forceErrorCustomerId),
  takeLatest(FORCEERROR_RESET, forceErrorReset),
  takeLatest(EXCEPTION_ERROR, exceptionError),
  takeLatest(SELECT_TELKOMSEL_MENARIK_PRODUCT, selectTelkomselMenarik),
]
